import { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Check, Copy, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ENDPOINTS } from '../config';
import { BankAccountSetup } from './BankAccountSetup';
import { PaymentMethodIcon } from './CardBrandIcon';
import { WalletTosGate } from './WalletTosGate';
import {
  getBalancePurchaseBonusPercent,
  getBalanceMinPurchaseCents,
  getBalanceMaxCents,
  getEnableCardAddFunds,
} from '../config/featureFlags';
import { useFlag } from '../contexts/FeatureFlagOverrideContext';
import { STRIPE_PUBLISHABLE_KEY } from '../utils/env';
import { calculateProcessingFeeCents, isLinkBankBrand, LINK_BANK_CARD_NAME, processingFeeLabel } from '../utils/stripeFee';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

type PaymentMethodType = 'card' | 'ach' | 'zelle';

type PendingZelleIntent = {
  id: string;
  amount_cents: number;
  memo_code: string;
  expires_at: string;
};

export interface WalletAddFundsProps {
  parentId: string;
  currentBalanceCents: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const PRESETS = [
  { label: '$1,000', cents: 100000 },
  { label: '$2,000', cents: 200000 },
  { label: '$5,000', cents: 500000 },
  { label: '$10,000', cents: 1000000 },
];

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

type ZelleInfo = {
  memo_code: string;
  instructions: string;
  expires_at: string;
};

function WalletAddFundsInner({
  parentId,
  currentBalanceCents,
  onSuccess,
  onCancel,
}: WalletAddFundsProps) {
  const stripe = useStripe();
  const elements = useElements();

  const minCents = getBalanceMinPurchaseCents();
  const maxCents = getBalanceMaxCents();
  const bonusPct = getBalancePurchaseBonusPercent();

  const [amountCents, setAmountCents] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  // Card on Add Funds is gated behind a flag (default off in settings.json). Admins can
  // flip it On for their own session via the feature-flag override panel; parents only
  // see Card once it's enabled. When disabled, parents add funds via ACH or Zelle (both
  // fee-free). Does not affect invoice checkout.
  const cardAddFundsEnabled = useFlag('enableCardAddFunds', getEnableCardAddFunds());
  const availableMethods: PaymentMethodType[] = cardAddFundsEnabled
    ? ['card', 'ach', 'zelle']
    : ['ach', 'zelle'];
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>(
    cardAddFundsEnabled ? 'card' : 'ach',
  );
  const [step, setStep] = useState<'select' | 'zelle_pending' | 'success' | 'ach_processing'>('select');
  const [zelleInfo, setZelleInfo] = useState<ZelleInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedMemo, setCopiedMemo] = useState(false);
  const [pendingIntents, setPendingIntents] = useState<PendingZelleIntent[]>([]);
  const [savedCard, setSavedCard] = useState<{
    stripe_payment_method_id: string;
    brand: string | null;
    last4: string | null;
  } | null>(null);
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [bankAccountSetup, setBankAccountSetup] = useState<{
    id: string;
    setup_intent_id: string;
    bank_name: string | null;
    account_type: string | null;
    last4: string | null;
  } | null>(null);
  const [isBankAccountLoading, setIsBankAccountLoading] = useState(true);

  const bonusCents = bonusPct > 0 ? Math.floor((amountCents * bonusPct) / 100) : 0;
  // Card carries the Stripe processing fee (charged on top of the principal), priced by
  // the saved card's brand (a `link` card is a bank payment at 2.6%); a newly entered card
  // is a card. ACH and Zelle are fee-free. Kept in sync with the balance-purchase edge function.
  const chargedCardBrand = savedCard && useSavedCard ? savedCard.brand : null;
  const feeCents = paymentMethod === 'card' ? calculateProcessingFeeCents(amountCents, chargedCardBrand) : 0;
  const totalChargedCents = amountCents + feeCents;

  const amountError: string | null = (() => {
    if (amountCents <= 0) return null;
    if (amountCents < minCents)
      return `Minimum purchase is ${formatUsdFromCents(minCents)}.`;
    if (currentBalanceCents + amountCents > maxCents)
      return `This would exceed your balance limit of ${formatUsdFromCents(maxCents)}.`;
    return null;
  })();

  useEffect(() => {
    (async () => {
      const { data: card } = await supabase
        .from('card_payment_methods')
        .select('stripe_payment_method_id, brand, last4')
        .eq('parent_id', parentId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .maybeSingle();
      if (card) {
        const c = card as {
          stripe_payment_method_id: string;
          brand: string | null;
          last4: string | null;
        };
        setSavedCard({
          stripe_payment_method_id: c.stripe_payment_method_id,
          brand: c.brand,
          last4: c.last4,
        });
      }
    })();
  }, [parentId]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('bank_account_setup_intents')
        .select('id, setup_intent_id, bank_name, account_type, last4')
        .eq('parent_id', parentId)
        .eq('is_active', true)
        .eq('status', 'succeeded')
        .order('created_at', { ascending: false })
        .maybeSingle();
      setBankAccountSetup(
        data as {
          id: string;
          setup_intent_id: string;
          bank_name: string | null;
          account_type: string | null;
          last4: string | null;
        } | null,
      );
      setIsBankAccountLoading(false);
    })();
  }, [parentId]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('balance_zelle_intents')
        .select('id, amount_cents, memo_code, expires_at')
        .eq('parent_id', parentId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (data) setPendingIntents(data as PendingZelleIntent[]);
    })();
  }, [parentId]);

  // Pre-fill amount from existing pending intent when Zelle tab is selected.
  useEffect(() => {
    if (paymentMethod === 'zelle' && pendingIntents.length > 0) {
      setAmountCents(pendingIntents[0].amount_cents);
      setCustomAmount('');
    }
  }, [paymentMethod, pendingIntents]);

  const handlePreset = (cents: number) => {
    setAmountCents(cents);
    setCustomAmount('');
  };

  const handleCustomAmount = (val: string) => {
    setCustomAmount(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed > 0) {
      setAmountCents(Math.round(parsed * 100));
    } else {
      setAmountCents(0);
    }
  };

  const copyMemo = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedMemo(true);
      setTimeout(() => setCopiedMemo(false), 2000);
    } catch {
      // ignore — user can manually copy
    }
  };

  const handleSubmit = async () => {
    if (!stripe) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }
    if (amountCents <= 0) {
      setError('Please enter an amount.');
      return;
    }
    if (amountError) {
      setError(amountError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated. Please log in and try again.');

      const res = await fetch(ENDPOINTS.balancePurchase, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          amount_cents: amountCents,
          ...(paymentMethod === 'zelle' && pendingIntents.length > 0
            ? { existing_zelle_intent_id: pendingIntents[0].id }
            : {}),
          ...(paymentMethod === 'ach' && bankAccountSetup
            ? { setup_intent_id: bankAccountSetup.setup_intent_id }
            : {}),
          // Lets the server price the fee by the saved card that will be charged
          ...(paymentMethod === 'card' && savedCard && useSavedCard
            ? { card_payment_method_id: savedCard.stripe_payment_method_id }
            : {}),
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status}). Please try again.`);
      }

      const result = (await res.json()) as {
        type: 'card_ach' | 'zelle';
        client_secret?: string;
        memo_code?: string;
        instructions?: string;
        expires_at?: string;
      };

      if (result.type === 'zelle') {
        setZelleInfo({
          memo_code: result.memo_code ?? '',
          instructions: result.instructions ?? '',
          expires_at: result.expires_at ?? '',
        });
        setStep('zelle_pending');
        return;
      }

      // Card or ACH — confirm with Stripe
      const clientSecret = result.client_secret!;

      if (paymentMethod === 'card') {
        let confirmResult;
        if (savedCard && useSavedCard) {
          confirmResult = await stripe.confirmCardPayment(clientSecret, {
            payment_method: savedCard.stripe_payment_method_id,
          });
        } else {
          const cardElement = elements?.getElement(CardElement);
          if (!cardElement) throw new Error('Card element not found.');
          confirmResult = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
          });
        }
        if (confirmResult.error) throw new Error(confirmResult.error.message);
      } else {
        // ACH
        const confirmResult = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/wallet?balance_updated=1`,
          },
          redirect: 'if_required',
        });
        if (confirmResult.error) throw new Error(confirmResult.error.message);

        // Check if ACH is in processing state (for pre-verified bank accounts)
        if (confirmResult.paymentIntent?.status === 'processing') {
          setStep('ach_processing');
          // Do NOT call onSuccess() — balance hasn't changed yet
          return;
        }
      }

      setStep('success');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'ach_processing') {
    return (
      <div className="py-8 text-center" data-testid="wallet-add-funds-ach-processing">
        <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
          <Check className="w-6 h-6 text-blue-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">ACH payment submitted</h2>
        <p className="text-sm text-gray-600 mb-1">
          Your payment of {formatUsdFromCents(amountCents)} is being processed.
        </p>
        <p className="text-sm text-gray-600 mb-6">
          Funds will appear in your BHCS Cash balance in 3–5 business days once the bank transfer clears.
        </p>
        <button
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          Back to BHCS Cash
        </button>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="py-8 text-center" data-testid="wallet-add-funds-success">
        <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <Check className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Funds added!</h2>
        <p className="text-sm text-gray-600 mb-1">
          {formatUsdFromCents(amountCents)} has been added to your BHCS Cash balance.
        </p>
        {bonusCents > 0 && (
          <p className="text-sm text-amber-700 mb-4">
            Plus {formatUsdFromCents(bonusCents)} bonus credit!
          </p>
        )}
        <p className="text-xs text-gray-500 mb-6">
          Bonus credit is non-withdrawable and has no cash value. BHCS Cash is not FDIC-insured.
        </p>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          Back to BHCS Cash
        </button>
      </div>
    );
  }

  if (step === 'zelle_pending') {
    return (
      <div data-testid="wallet-add-funds-zelle-pending">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Zelle payment instructions</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700">{zelleInfo?.instructions}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-xs text-gray-500 mb-1">Your memo code</p>
          <div className="flex items-center gap-2">
            <span
              className="text-xl font-mono font-semibold text-gray-900"
              data-testid="wallet-zelle-memo-code"
            >
              {zelleInfo?.memo_code}
            </span>
            <button
              type="button"
              onClick={() => copyMemo(zelleInfo?.memo_code ?? '')}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Copy memo code"
            >
              {copiedMemo ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
          {zelleInfo?.expires_at && (
            <p className="text-xs text-gray-500 mt-1">
              Expires: {new Date(zelleInfo.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-6">
          We'll credit your BHCS Cash automatically — usually within a few minutes once your Zelle payment is received.
        </p>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          Back to BHCS Cash
        </button>
      </div>
    );
  }

  const canSubmit = amountCents > 0 && !amountError && !isSubmitting;

  return (
    <div data-testid="wallet-add-funds-form">
      {/* Amount */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">Amount</label>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {PRESETS.map((p) => (
            <button
              key={p.cents}
              type="button"
              onClick={() => handlePreset(p.cents)}
              className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                amountCents === p.cents && !customAmount
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
          <input
            type="number"
            min="0"
            step="1"
            value={customAmount}
            onChange={(e) => handleCustomAmount(e.target.value)}
            placeholder="Custom amount"
            className="w-full pl-7 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            data-testid="wallet-add-funds-custom-amount"
          />
        </div>
        {amountError && (
          <p className="mt-1 text-xs text-red-600" data-testid="wallet-add-funds-amount-error">
            {amountError}
          </p>
        )}
        {bonusPct > 0 && amountCents > 0 && !amountError && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-800">
              You'll receive a <strong>{bonusPct}% bonus</strong> (
              {formatUsdFromCents(bonusCents)}) in non-withdrawable BHCS Cash!
            </p>
          </div>
        )}
      </div>

      {/* Payment method */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">Payment method</label>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          {availableMethods.map((pm, i) => (
            <button
              key={pm}
              type="button"
              onClick={() => setPaymentMethod(pm)}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                i > 0 ? 'border-l border-gray-300' : ''
              } ${
                paymentMethod === pm
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`wallet-pm-${pm}`}
            >
              {pm === 'card' ? 'Card' : pm === 'ach' ? 'ACH' : 'Zelle'}
            </button>
          ))}
        </div>
      </div>

      {/* Card element */}
      {paymentMethod === 'card' && (
        <div className="mb-6">
          {savedCard ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setUseSavedCard(true)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                  useSavedCard
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <PaymentMethodIcon type="card" brand={savedCard.brand ?? undefined} size={26} />
                <span className="text-sm font-medium text-gray-900">
                  {isLinkBankBrand(savedCard.brand) ? LINK_BANK_CARD_NAME : `${savedCard.brand} •••• ${savedCard.last4}`}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setUseSavedCard(false)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                  !useSavedCard
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <PaymentMethodIcon type="card" size={26} />
                <span className="text-sm font-medium text-gray-900">Use a different card</span>
              </button>
              {!useSavedCard && (
                <div className="p-3 border border-gray-300 rounded-md mt-1">
                  <CardElement options={{ style: { base: { fontSize: '14px' } } }} />
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 border border-gray-300 rounded-md">
              <CardElement options={{ style: { base: { fontSize: '14px' } } }} />
            </div>
          )}
        </div>
      )}

      {paymentMethod === 'ach' && (
        <div className="mb-6">
          {isBankAccountLoading ? (
            <div className="p-4 border rounded-lg bg-gray-50 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Checking bank account...</p>
            </div>
          ) : bankAccountSetup ? (
            <div className="px-4 py-3.5 border border-gray-200 rounded-xl bg-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center min-w-0">
                  <div className="mr-3 shrink-0">
                    <PaymentMethodIcon type="bank_account" size={26} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                      {bankAccountSetup.bank_name ?? 'Bank'} •••• {bankAccountSetup.last4 ?? '••••'}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs sm:text-sm text-gray-500">
                      <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Verified
                      </span>
                      {bankAccountSetup.account_type && <span>{bankAccountSetup.account_type}</span>}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase
                      .from('bank_account_setup_intents')
                      .update({ is_active: false })
                      .eq('id', bankAccountSetup.id);
                    setBankAccountSetup(null);
                  }}
                  className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2.5">ACH transfers may take 3–5 business days to process.</p>
            </div>
          ) : (
            <Elements stripe={stripePromise}>
              <BankAccountSetup
                returnPath="/wallet"
                onSetupComplete={() => {
                  setIsBankAccountLoading(true);
                  supabase
                    .from('bank_account_setup_intents')
                    .select('id, setup_intent_id, bank_name, account_type, last4')
                    .eq('parent_id', parentId)
                    .eq('is_active', true)
                    .eq('status', 'succeeded')
                    .order('created_at', { ascending: false })
                    .maybeSingle()
                    .then(({ data }) => {
                      setBankAccountSetup(
                        data as {
                          id: string;
                          setup_intent_id: string;
                          bank_name: string | null;
                          account_type: string | null;
                          last4: string | null;
                        } | null,
                      );
                      setIsBankAccountLoading(false);
                    });
                }}
              />
            </Elements>
          )}
        </div>
      )}

      {paymentMethod === 'zelle' && (
        <div className="mb-6">
          {pendingIntents.length > 0 ? (
            <div
              className="border border-gray-200 rounded-lg p-4"
              data-testid="wallet-add-funds-pending-intents"
            >
              <p className="text-sm font-medium text-gray-900 mb-1">Your pending Zelle payment</p>
              <p className="text-xs text-gray-500 mb-3">
                Send <strong>{formatUsdFromCents(pendingIntents[0].amount_cents)}</strong> via
                Zelle to <strong>billing@bhcskids.com</strong> using this memo code.
                Your balance is credited automatically within a few minutes.
              </p>
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="text-xl font-mono font-semibold text-gray-900"
                  data-testid="wallet-add-funds-pending-memo"
                >
                  {pendingIntents[0].memo_code}
                </span>
                <button
                  type="button"
                  onClick={() => copyMemo(pendingIntents[0].memo_code)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Copy memo code"
                >
                  {copiedMemo ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                To send a different amount, adjust the amount above then click Update below.
              </p>
              {error && (
                <p className="mb-3 text-sm text-red-600" data-testid="wallet-add-funds-error">
                  {error}
                </p>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="wallet-add-funds-submit"
              >
                {isSubmitting ? 'Processing...' : 'Update Zelle Payment'}
              </button>
            </div>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                After confirming, you'll receive a unique memo code to include with your Zelle
                payment to billing@bhcskids.com. Your balance is typically credited within a few
                minutes.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Payment breakdown */}
      {(paymentMethod === 'card' || paymentMethod === 'ach') && amountCents > 0 && !amountError && (
        <div
          className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm"
          data-testid="wallet-add-funds-breakdown"
        >
          <div className="flex justify-between text-gray-700">
            <span>Add to BHCS Cash</span>
            <span>{formatUsdFromCents(amountCents)}</span>
          </div>
          {bonusCents > 0 && (
            <div className="flex justify-between text-gray-700">
              <span>Bonus credit ({bonusPct}%)</span>
              <span className="text-green-700">+{formatUsdFromCents(bonusCents)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 text-gray-500">
            <span>In your balance</span>
            <span>{formatUsdFromCents(amountCents + bonusCents)}</span>
          </div>
          {feeCents > 0 && (
            <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 text-gray-700">
              <span>Processing fee ({processingFeeLabel(chargedCardBrand)})</span>
              <span data-testid="wallet-add-funds-fee">+{formatUsdFromCents(feeCents)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between font-semibold text-gray-900">
            <span>{paymentMethod === 'card' ? 'Total charged to your card' : 'Total charged to your bank'}</span>
            <span data-testid="wallet-add-funds-total">{formatUsdFromCents(totalChargedCents)}</span>
          </div>
        </div>
      )}

      {/* Disclosure */}
      <p className="text-xs text-gray-500 mb-4">
        Bonus credit is non-withdrawable and has no cash value. BHCS Cash is not FDIC-insured.
      </p>

      {!(paymentMethod === 'zelle' && pendingIntents.length > 0) &&
       !(paymentMethod === 'ach' && !isBankAccountLoading && !bankAccountSetup) && (
        <>
          {error && (
            <p className="mb-4 text-sm text-red-600" data-testid="wallet-add-funds-error">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full py-3 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="wallet-add-funds-submit"
          >
            {isSubmitting
              ? 'Processing...'
              : paymentMethod === 'zelle'
              ? 'Get Zelle Instructions'
              : `Add ${amountCents > 0 ? formatUsdFromCents(amountCents) : ''} to BHCS Cash`}
          </button>
        </>
      )}
    </div>
  );
}

export function WalletAddFunds(props: WalletAddFundsProps) {
  return (
    <WalletTosGate parentId={props.parentId}>
      <Elements stripe={stripePromise}>
        <WalletAddFundsInner {...props} />
      </Elements>
    </WalletTosGate>
  );
}

